import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";
import type { Division, Estate } from "../lib/types";

interface DivisionFormProps {
  initial?: Division;
  defaultEstateId?: string;
  onSaved: (division: Division) => void;
  onCancel: () => void;
}

export function DivisionForm({ initial, defaultEstateId, onSaved, onCancel }: DivisionFormProps) {
  const isEdit = Boolean(initial);
  const qc = useQueryClient();

  const [estateId, setEstateId] = useState(initial?.estateId ?? defaultEstateId ?? "");
  const [code, setCode] = useState(initial?.code ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const estatesQ = useQuery({
    queryKey: ["estates"],
    queryFn: () => api.list<Estate>("/v1/estates"),
  });

  const submitMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { code, name };
      if (!isEdit) payload.estateId = estateId;
      if (isEdit) {
        const res = await api.patch<Division>(`/v1/divisions/${initial!.id}`, payload);
        return res.data;
      }
      const res = await api.post<Division>("/v1/divisions", payload);
      return res.data;
    },
    onSuccess: (division) => {
      qc.invalidateQueries({ queryKey: ["divisions"] });
      onSaved(division);
    },
    onError: (err) => {
      setFieldErrors({});
      setFormError(null);
      if (err instanceof ApiError) {
        const d = err.details as { fieldErrors?: Record<string, string[]> } | undefined;
        if (d?.fieldErrors) setFieldErrors(d.fieldErrors);
        else setFormError(err.message);
      } else {
        setFormError((err as Error).message ?? "Unknown error");
      }
    },
  });

  const f = (name: string) => fieldErrors[name]?.[0];

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submitMut.mutate();
      }}
      className="space-y-4"
    >
      {formError && (
        <div className="bg-bg-danger text-text-danger border border-border-danger rounded p-2 text-xs">
          {formError}
        </div>
      )}

      <Row label="Estate" error={f("estateId")}>
        <select
          className="input"
          value={estateId}
          onChange={(e) => setEstateId(e.target.value)}
          disabled={isEdit || estatesQ.isLoading}
        >
          <option value="">— select —</option>
          {(estatesQ.data?.data ?? []).map((e) => (
            <option key={e.id} value={e.id}>
              {e.code} · {e.name}
            </option>
          ))}
        </select>
      </Row>

      <div className="grid grid-cols-2 gap-3">
        <Row label="Division code" error={f("code")} hint="1-2 chars, e.g. A, B, C">
          <input
            className="input font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={2}
          />
        </Row>
        <Row label="Division name" error={f("name")}>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Row>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={submitMut.isPending}>
          {submitMut.isPending ? "Saving…" : isEdit ? "Save changes" : "Create division"}
        </button>
      </div>
    </form>
  );
}

function Row({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1">{label}</label>
      {children}
      {hint && !error && <div className="text-[10.5px] text-text-muted mt-1">{hint}</div>}
      {error && <div className="text-[10.5px] text-text-danger mt-1">{error}</div>}
    </div>
  );
}
